import React, { useState, useEffect, useMemo } from "react";
import { 
  FaSearch, FaChevronDown, FaChevronUp, FaPrint, FaReceipt, FaSync 
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axiosInstance from "../../api/axiosInstance";
import Pagination from "../Pagination/Pagination";
import "./OnlineReceipt.css";

const OnlineReceipt = () => {
  const navigate = useNavigate();
  const [donors, setDonors] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [dateFilter, setDateFilter] = useState(""); 
  const [sortOrder, setSortOrder] = useState("desc"); // 'desc' for latest first, 'asc' for oldest first

  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchDonors();
  }, []);

  const fetchDonors = async () => {
    try {
      const res = await axiosInstance.get("/api/donors");
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setDonors(list);
    } catch (err) {
      console.error("Error fetching donors data:", err);
    }
  };

  // Extract, filter, and sequence successful online donations
  const onlineReceipts = useMemo(() => {
    let list = [];
    donors.forEach(donor => {
      if (donor.isDeleted) return;
      
      (donor.donations || []).forEach(don => {
        // Successful online payment gateway transactions
        const isOnline = don.mode === "Online Gateway" || (don.receiptNo && (don.receiptNo.startsWith("RSK-ONL-") || don.receiptNo.startsWith("RSK-PUB-")));
        const isSuccess = don.txnStatus === "Success" || !don.txnStatus;

        if (isSuccess && isOnline) {
          list.push({
            _id: don._id,
            donorId: donor._id,
            name: donor.name,
            fatherName: donor.fatherName,
            mobileNo: donor.mobileNo,
            memberId: donor.memberId,
            donationType: don.donationType,
            amount: don.amount,
            date: don.date,
            receiptNo: don.receiptNo,
            mode: don.mode,
            refNo: don.refNo,
            pgPaymentMode: don.pgPaymentMode,
            pgPaymentId: don.pgPaymentId,
            merchantTxnNo: don.merchantTxnNo,
            bankTxnDate: don.bankTxnDate || null,
            isActive: don.isActive !== false
          });
        }
      });
    });

    // Apply Search Filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(l => 
        (l.memberId || "").toLowerCase().includes(q) || 
        (l.name || "").toLowerCase().includes(q) || 
        (l.mobileNo || "").includes(q) ||
        (l.receiptNo || "").toLowerCase().includes(q)
      );
    }

    // Apply Donation Type Filter
    if (typeFilter) {
      list = list.filter(l => l.donationType === typeFilter);
    }

    // Apply Specific Date Filter
    if (dateFilter) {
      list = list.filter(l => {
        const d = new Date(l.date);
        const localDateStr = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
        return localDateStr === dateFilter;
      });
    }

    // Sort: Newest vs Oldest First
    return list.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [donors, searchQuery, typeFilter, dateFilter, sortOrder]);

  const paginatedReceipts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return onlineReceipts.slice(startIndex, startIndex + itemsPerPage);
  }, [onlineReceipts, currentPage, itemsPerPage]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = d.toLocaleString('en-IN', { month: 'short', timeZone: 'UTC' });
    return `${day} ${month} ${d.getUTCFullYear()}`;
  };

  const formatAmount = (amt) => {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amt || 0);
  };

  return (
    <>
      <Helmet>
        <title>Online Receipts | Rajput Shiksha Kosh</title>
      </Helmet>

      <div className="donation-online-receipt-page-container">
        <h2 className="donation-online-receipt-page-title">
          <FaReceipt className="donation-online-receipt-page-title-icon" /> Online Receipts Dashboard
        </h2>

        {/* Filters and Controls */}
        <div className="donation-online-receipt-page-controls">
          <div className="donation-online-receipt-page-search-wrapper">
            <FaSearch className="donation-online-receipt-page-search-icon" />
            <input 
              type="text" 
              className="donation-online-receipt-page-search-input" 
              placeholder="Search Receipt No, Member ID, or Name..." 
              value={searchQuery} 
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }} 
            />
          </div>

          <div className="donation-online-receipt-page-right-controls">
            <input 
              type="date" 
              value={dateFilter} 
              onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }} 
              className="donation-online-receipt-page-items-select" 
              title="Filter by Specific Date" 
            />
            
            <select 
              value={typeFilter} 
              onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }} 
              className="donation-online-receipt-page-items-select"
            >
              <option value="">All Donation Types</option>
              <option value="General Donation (सामान्य दान)">General Donation (सामान्य दान)</option>
              <option value="Shiksha Nek (शिक्षा दान)">Shiksha Nek (शिक्षा दान)</option>
              <option value="Encouragement Fund (प्रोत्साहन निधि)">Encouragement Fund (प्रोत्साहन निधि)</option>
            </select>

            <select 
              value={sortOrder} 
              onChange={(e) => { setSortOrder(e.target.value); setCurrentPage(1); }} 
              className="donation-online-receipt-page-items-select"
            >
              <option value="desc">Newest First (नवीनतम)</option>
              <option value="asc">Oldest First (पुरातन)</option>
            </select>

            <select 
              value={itemsPerPage} 
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} 
              className="donation-online-receipt-page-items-select"
            >
              <option value="5">5 / page</option>
              <option value="10">10 / page</option>
              <option value="20">20 / page</option>
              <option value="50">50 / page</option>
            </select>

            <button className="donation-online-receipt-page-btn-refresh" onClick={fetchDonors} title="Reload Receipt List">
              <FaSync />
            </button>
          </div>
        </div>

        {/* Table wrapper */}
        <div className="donation-online-receipt-page-table-wrapper">
          <table className="donation-online-receipt-page-table">
            <thead>
              <tr>
                <th className="donation-online-receipt-page-col-sno">S.No</th>
                <th className="donation-online-receipt-page-col-receipt">Receipt Number</th>
                <th className="donation-online-receipt-page-col-date">Date</th>
                <th className="donation-online-receipt-page-col-amount">Amount</th>
                <th className="donation-online-receipt-page-col-memberid">Member ID</th>
                <th className="donation-online-receipt-page-col-name text-left">Member Name</th>
                <th className="donation-online-receipt-page-col-type">Donation Type</th>
                <th className="donation-online-receipt-page-col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedReceipts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="donation-online-receipt-page-empty-fallback">
                    No online transaction receipts found matching current filters.
                  </td>
                </tr>
              ) : paginatedReceipts.map((rec, index) => {
                const serialNo = (currentPage - 1) * itemsPerPage + index + 1;

                return (
                  <React.Fragment key={rec._id}>
                    <tr className="donation-online-receipt-page-main-row">
                      <td className="donation-online-receipt-page-col-sno">{serialNo}</td>
                      
                      <td className="donation-online-receipt-page-col-receipt">
                        <div className="donation-online-receipt-page-receipt-cell">
                          <span className="donation-online-receipt-page-receipt-badge">
                            {rec.receiptNo}
                          </span>
                          <button 
                            className="donation-online-receipt-page-expand-btn"
                            onClick={() => setExpandedRow(expandedRow === rec._id ? null : rec._id)}
                          >
                            {expandedRow === rec._id ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                        </div>
                      </td>

                      <td className="donation-online-receipt-page-col-date">{formatDate(rec.date)}</td>
                      
                      <td className="donation-online-receipt-page-col-amount font-bold">
                        {formatAmount(rec.amount)}
                      </td>
                      
                      <td className="donation-online-receipt-page-col-memberid font-bold-na">
                        {rec.memberId || "N/A"}
                      </td>
                      
                      <td className="donation-online-receipt-page-col-name text-left">
                        <div className="donation-online-receipt-page-name-block">
                          <span className="font-semibold">{rec.name}</span>
                          <span className="subtext">{rec.mobileNo}</span>
                        </div>
                      </td>
                      
                      <td className="donation-online-receipt-page-col-type">
                        <span className="donation-online-receipt-page-badge-info">
                          {rec.donationType}
                        </span>
                      </td>

                      <td className="donation-online-receipt-page-col-action">
                        <button 
                          className="donation-online-receipt-page-btn-print"
                          onClick={() => navigate("/admin/donation-receipt", { state: { donation: rec } })}
                          title="Print Official Online Receipt"
                        >
                          <FaPrint /> Print
                        </button>
                      </td>
                    </tr>

                    {expandedRow === rec._id && (
                      <tr className="donation-online-receipt-page-expanded-row">
                        <td colSpan="8">
                          <div className="donation-online-receipt-page-expanded-content">
                            
                            {/* Hidden components displayed on smaller viewports dynamically */}
                            <div className="donation-online-receipt-page-exp-item donation-online-receipt-page-exp-date">
                              <span className="donation-online-receipt-page-exp-label">Date</span>
                              <span className="donation-online-receipt-page-exp-value">{formatDate(rec.date)}</span>
                            </div>

                            <div className="donation-online-receipt-page-exp-item donation-online-receipt-page-exp-amount">
                              <span className="donation-online-receipt-page-exp-label">Amount</span>
                              <span className="donation-online-receipt-page-exp-value font-bold">{formatAmount(rec.amount)}</span>
                            </div>

                            <div className="donation-online-receipt-page-exp-item donation-online-receipt-page-exp-memberid">
                              <span className="donation-online-receipt-page-exp-label">Member ID</span>
                              <span className="donation-online-receipt-page-exp-value font-bold-na">{rec.memberId || "N/A"}</span>
                            </div>

                            <div className="donation-online-receipt-page-exp-item donation-online-receipt-page-exp-name">
                              <span className="donation-online-receipt-page-exp-label">Member Name</span>
                              <span className="donation-online-receipt-page-exp-value">{rec.name} ({rec.mobileNo})</span>
                            </div>

                            <div className="donation-online-receipt-page-exp-item donation-online-receipt-page-exp-type">
                              <span className="donation-online-receipt-page-exp-label">Donation Type</span>
                              <span className="donation-online-receipt-page-exp-value">{rec.donationType}</span>
                            </div>

                            {/* Additional Gateway/Transaction Specific Information */}
                            <div className="donation-online-receipt-page-exp-item">
                              <span className="donation-online-receipt-page-exp-label">Bank Transaction ID</span>
                              <span className="donation-online-receipt-page-exp-value select-all">{rec.refNo || "N/A"}</span>
                            </div>

                            <div className="donation-online-receipt-page-exp-item">
                              <span className="donation-online-receipt-page-exp-label">Bank Transaction Date</span>
                              <span className="donation-online-receipt-page-exp-value">{rec.bankTxnDate ? formatDate(rec.bankTxnDate) : "N/A"}</span>
                            </div>

                            <div className="donation-online-receipt-page-exp-item">
                              <span className="donation-online-receipt-page-exp-label">Gateway Method</span>
                              <span className="donation-online-receipt-page-exp-value">{rec.pgPaymentMode || "UPI/Card"}</span>
                            </div>

                            <div className="donation-online-receipt-page-exp-item">
                              <span className="donation-online-receipt-page-exp-label">UPI / Masked Card ID</span>
                              <span className="donation-online-receipt-page-exp-value">{rec.pgPaymentId || "N/A"}</span>
                            </div>

                            <div className="donation-online-receipt-page-exp-item">
                              <span className="donation-online-receipt-page-exp-label">Order Ref No</span>
                              <span className="donation-online-receipt-page-exp-value select-all">{rec.merchantTxnNo || "N/A"}</span>
                            </div>

                            <div className="donation-online-receipt-page-exp-item donation-online-receipt-page-exp-action">
                              <span className="donation-online-receipt-page-exp-label">Receipt</span>
                              <div className="donation-online-receipt-page-exp-actions-wrapper">
                                <button 
                                  className="donation-online-receipt-page-btn-print"
                                  onClick={() => navigate("/admin/donation-receipt", { state: { donation: rec } })}
                                  title="Print Official Online Receipt"
                                >
                                  <FaPrint /> Print Receipt
                                </button>
                              </div>
                            </div>

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination 
          totalItems={onlineReceipts.length} 
          itemsPerPage={itemsPerPage} 
          currentPage={currentPage} 
          onPageChange={setCurrentPage} 
        />
      </div>
    </>
  );
};

export default OnlineReceipt;